import { Component } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

/**
 * Catches render errors so one broken component does not blank the whole app.
 *
 * A white screen tells the user nothing and tells support less. This at least
 * names what happened, offers a way out, and shows the stack in development
 * where it is useful.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // In production this is where a reporting service would be called. Logging
    // it keeps the detail reachable from the browser console meanwhile.
    console.error('[ErrorBoundary]', error, info?.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-surface-sunken)] p-6">
        <div className="w-full max-w-md rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-6 text-center">
          <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-danger-bg)]">
            <AlertTriangle size={20} style={{ color: 'var(--color-danger)' }} aria-hidden="true" />
          </div>

          <h1 className="font-display text-lg font-semibold text-[var(--color-ink-900)]">
            Something went wrong
          </h1>

          <p className="mt-2 text-sm leading-relaxed text-[var(--color-ink-500)]">
            This screen failed to load. Your data has not been affected — nothing was saved or
            changed. Reloading usually fixes it.
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            <RefreshCw size={15} aria-hidden="true" />
            Reload the page
          </button>

          {import.meta.env.DEV && (
            <pre className="mt-5 max-h-52 overflow-auto rounded bg-[var(--color-surface-sunken)] p-3 text-left text-[0.6875rem] leading-relaxed text-[var(--color-ink-700)]">
              {this.state.error.stack ?? String(this.state.error)}
            </pre>
          )}
        </div>
      </div>
    )
  }
}
